import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ResizableMediaOptions, createResizableMediaView, createResizableMediaCommands } from './resizable-media-utils';

export interface AudioOptions extends ResizableMediaOptions {
  inline: boolean;
  allowBase64: boolean;
}

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    resizableAudio: {
      /**
       * Add a resizable audio
       */
      setResizableAudio: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        align?: string;
        controls?: boolean;
        preload?: 'none' | 'metadata' | 'auto';
      }) => ReturnType;
      /**
       * Update resizable audio width
       */
      updateResizableAudioWidth: (width: number) => ReturnType;
      /**
       * Update resizable audio alignment
       */
      updateResizableAudioAlign: (align: string) => ReturnType;
    };
  }
}

export const audioExtension = Node.create<AudioOptions>({
  name: 'resizable-audio',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {},
      minWidth: 10,
      maxWidth: 100,
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('src'),
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('alt'),
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          title: attributes.title,
        }),
      },
      width: {
        default: 100,
        parseHTML: (element: HTMLElement): number => {
          const width = element.getAttribute('data-width');
          return width ? parseInt(width, 10) : 100;
        },
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': attributes.width,
          style: `width: ${attributes.width}%;`,
        }),
      },
      align: {
        default: 'left',
        parseHTML: (element: HTMLElement): string => {
          const align = element.getAttribute('data-align');
          return align || 'left';
        },
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': attributes.align,
        }),
      },
      controls: {
        default: true,
        parseHTML: (element: HTMLElement): boolean => element.hasAttribute('controls'),
        renderHTML: (attributes: Record<string, string>): Record<string, string> => {
          if (attributes.controls) {
            return { controls: '' };
          }
          return {};
        },
      },

      preload: {
        default: 'metadata',
        parseHTML: (element: HTMLElement): string => element.getAttribute('preload') || 'metadata',
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          preload: attributes.preload,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="resizable-audio"]',
        getAttrs: (element: HTMLElement): Record<string, string | boolean | number> | false => {
          const audio = (element as HTMLElement).querySelector('audio');
          if (!audio) {
            return false;
          }

          let width = 100;
          const dataWidth = element.getAttribute('data-width');

          if (dataWidth) {
            width = parseInt(dataWidth, 10);
          } else {
            const style = element.getAttribute('style');
            const widthMatch = style?.match(/width:\s*(\d+)%/);
            if (widthMatch) {
              width = parseInt(widthMatch[1], 10);
            }
          }

          const align = element.getAttribute('data-align') || 'left';

          return {
            src: audio.getAttribute('src'),
            alt: audio.getAttribute('alt') || '',
            title: audio.getAttribute('title') || '',
            width,
            align,
            controls: true,
            preload: audio.getAttribute('preload') || 'metadata',
          };
        },
      },
      {
        tag: this.options.allowBase64 ? 'audio[src]' : 'audio[src]:not([src^="data:"])',
        getAttrs: (element: HTMLElement): Record<string, string | boolean | number> | false => {
          const audio = element as HTMLAudioElement;
          return {
            src: audio.getAttribute('src'),
            alt: audio.getAttribute('alt') || '',
            title: audio.getAttribute('title') || '',
            width: 100,
            align: 'left',
            controls: true,
            preload: audio.getAttribute('preload') || 'metadata',
          };
        },
      },
    ];
  },

  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes }) {
    const width = HTMLAttributes['data-width'] || 100;
    const align = HTMLAttributes['data-align'] || 'left';

    let alignStyle = '';
    switch (align) {
      case 'center':
        alignStyle = 'margin: 0 auto;';
        break;
      case 'right':
        alignStyle = 'margin-left: auto;';
        break;
      case 'left':
      default:
        alignStyle = 'margin-right: auto;';
        break;
    }

    return [
      'div',
      mergeAttributes(
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-type': 'resizable-audio',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': width,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': align,
          style: `width: 100%; display: block;`,
        },
        this.options.HTMLAttributes,
      ),
      [
        'audio',
        mergeAttributes(
          {
            src: HTMLAttributes.src,
            alt: HTMLAttributes.alt,
            title: HTMLAttributes.title,
            controls: HTMLAttributes.controls ? 'controls' : true,
            preload: HTMLAttributes.preload,
          },
          {
            style: `width: ${width}%; display: block; ${alignStyle}`,
            class: 'tiptap-audio',
          },
        ),
      ],
    ];
  },

  addNodeView() {
    return createResizableMediaView(
      {
        nodeName: 'audio',
        tagName: 'audio',
        extraAttributes: {
          controls: true,
          preload: 'metadata',
        },
      },
      this.options,
    );
  },

  addCommands() {
    return createResizableMediaCommands('audio');
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('resizable-audio'),
        props: {
          nodeViews: {},
        },
      }),
    ];
  },

  addInputRules() {
    return [
      // Règle pour insérer de l'audio avec markdown-like syntax
      // [audio](url) ou [audio title](url)
      // nodeInputRule({
      //   find: /!\[([^\]]*)\]\(([^)]+)\)$/,
      //   type: this.type,
      //   getAttributes: match => {
      //     const [, alt, src] = match;
      //     return { src, alt };
      //   },
      // }),
    ];
  },
});

export default audioExtension;
